"use client";

import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { MessageSquare, X } from "@/components/icons";
import { RepackifyMark } from "@/components/brand/repackify-logo";
import { ErrorBoundary } from "@/components/error/error-boundary";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { LoadFailureDialog } from "@/components/ui/load-failure-dialog";
import { useSearchContextSafe } from "@/components/search/search-context";
import { useActiveViewAs } from "@/components/view-as/use-active-view-as";
import { OPEN_SUPPORT_CHAT_EVENT } from "@/lib/chat/open-public-chat";
import { cn } from "@/lib/utils";
import { useChatNudge } from "./use-chat-nudge";
import { AskRepackifyChip } from "./ask-repackify-chip";

const FLOATING_CHIP_POSITION =
  "fixed bottom-[max(1rem,var(--safe-area-inset-bottom))] left-1/2 z-40 -translate-x-1/2 transition-[bottom] duration-200 lg:hidden [:root[data-sticky-cta=shown]_&]:bottom-[calc(5.5rem+var(--safe-area-inset-bottom))]";

const PublicAskPanel = lazy(() =>
  import("./public-ask-panel").then((module) => ({
    default: module.PublicAskPanel,
  })),
);


function useAskPanelRootFlag(visible: boolean, threadWanted: boolean) {
  useEffect(() => {
    const root = document.documentElement;
    if (visible) root.dataset.askPanel = "open";
    else if (threadWanted) root.dataset.askPanel = "hidden";
    else delete root.dataset.askPanel;
    return () => {
      delete root.dataset.askPanel;
    };
  }, [visible, threadWanted]);
}

function usePanelState(disabled: boolean) {
  const [chosen, setChosen] = useState<boolean | null>(null);
  const [threadWanted, setThreadWanted] = useState(false);
  const { publicChatMessage, clearPublicChatMessage } = useSearchContextSafe();

  if (!disabled && publicChatMessage && !(threadWanted && chosen === true)) {
    setThreadWanted(true);
    setChosen(true);
  }
  const visible = !disabled && chosen === true;
  useAskPanelRootFlag(visible, threadWanted);

  useEffect(() => {
    if (disabled && publicChatMessage) clearPublicChatMessage();
  }, [disabled, publicChatMessage, clearPublicChatMessage]);

  const show = useCallback(() => setChosen(true), []);
  const nudge = useChatNudge({ busy: visible || threadWanted, open: show });
  const dismissNudge = nudge.dismiss;
  const hide = useCallback(() => {
    setChosen(false);
    dismissNudge();
  }, [dismissNudge]);

  useEffect(() => {
    if (disabled) return;
    window.addEventListener(OPEN_SUPPORT_CHAT_EVENT, show);
    return () => window.removeEventListener(OPEN_SUPPORT_CHAT_EVENT, show);
  }, [disabled, show]);

  return { visible, mounted: chosen !== null, requested: chosen === true, threadWanted, show, hide, nudge };
}

function DismissOnFailure({ onDismiss }: { onDismiss: () => void }) {
  useEffect(onDismiss, [onDismiss]);
  return null;
}

function AskPanelSlot({ panel }: { panel: ReturnType<typeof usePanelState> }) {
  return (
    <ErrorBoundary
      label="public-ask-panel"
      fallback={
        panel.requested ? (
          <LoadFailureDialog
            title="Ask Repackify could not load"
            open={panel.visible}
            onOpenChange={(open) => {
              if (!open) panel.hide();
            }}
          />
        ) : (
          <DismissOnFailure onDismiss={panel.hide} />
        )
      }
    >
      <Suspense fallback={null}>
        <PublicAskPanel
          visible={panel.visible}
          requested={panel.requested && !panel.nudge.autoOpened}
          welcome={panel.nudge.nudge}
          threadWanted={panel.threadWanted}
          onHide={panel.hide}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

export function PublicChatWidget() {
  const viewingAs = useActiveViewAs();
  const panel = usePanelState(Boolean(viewingAs));

  if (viewingAs) return null;

  return (
    <>
      {!panel.visible && (
        <AskRepackifyChip onClick={panel.show} />
      )}
      {!panel.visible && <FloatingChip panel={panel} />}
      {panel.mounted && <AskPanelSlot panel={panel} />}
    </>
  );
}

function FloatingChip({ panel }: { panel: ReturnType<typeof usePanelState> }) {
  if (panel.threadWanted) {
    return (
      <Button
        variant="chip"
        size="chip"
        className={cn(FLOATING_CHIP_POSITION, "min-h-12 whitespace-nowrap bg-surface px-5 text-base shadow-modal")}
        onClick={panel.show}
      >
        <MessageSquare className="size-[18px]" aria-hidden />
        Back to chat
      </Button>
    );
  }
  if (!panel.nudge.chipShown || !panel.nudge.nudge) return null;
  return <NudgeChip label={panel.nudge.nudge.chip} onOpen={panel.nudge.openFromChip} onDismiss={panel.nudge.dismiss} />;
}

function NudgeChip({ label, onOpen, onDismiss }: { label: string; onOpen: () => void; onDismiss: () => void }) {
  return (
    <div
      role="region"
      aria-label="Ask Repackify"
      className={cn(FLOATING_CHIP_POSITION, "flex items-center rounded-full border border-border bg-surface shadow-modal")}
    >
      <Button variant="ghost" className="min-h-12 rounded-full pr-2 pl-5 text-base whitespace-nowrap" onClick={onOpen}>
        <RepackifyMark alt="" className="h-4" />
        {label}
      </Button>
      <IconButton aria-label="Dismiss" size="round-sm" className="mr-1" onClick={onDismiss}>
        <X className="size-4" aria-hidden />
      </IconButton>
    </div>
  );
}
